import { BaseButton } from '../base/button/button';
import { mdiRefresh } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { isAgentReady, profilesUsingAgent, profilesUsingModel } from '../../services/ai/ai.derive';
import { AiStore } from '../../stores/ai/ai.store';
import { StatusTag } from './ai-fields';
import * as S from './ai.styles';

const AGENT_COLUMNS = 'minmax(140px,1fr) minmax(170px,1.2fr) minmax(250px,1.6fr) 90px 130px minmax(120px,1fr)';
const MODEL_COLUMNS = 'minmax(200px,1fr) minmax(160px,1fr)';

/** Profile IDs that reference an entry, or "—" when none. "Used by" reads the SAVED configuration. */
const usedBy = (ids: string[]): string => (ids.length ? ids.join(', ') : '—');

export const CatalogTab = observer(({ store }: { store: AiStore }): React.ReactElement => {
    const { agents, models, saved } = store;
    return (
        <S.Stack>
            <S.Table>
                <S.TableHead>
                    <S.Grow>
                        <S.CardTitle>Agents</S.CardTitle>
                        <S.CardSub>
                            Registered in this environment&apos;s LiteLLM agent registry. Registration does not grant access;
                            profiles do.
                        </S.CardSub>
                    </S.Grow>
                    <BaseButton variant="outlined" size="compact" onClick={(): void => void store.refreshAgents()}>
                        <S.ButtonContent>
                            <Icon path={mdiRefresh} size={0.65} />
                            Recheck availability
                        </S.ButtonContent>
                    </BaseButton>
                </S.TableHead>
                {store.catalogErrors.agents && (
                    <S.Empty>
                        The agent list could not be loaded: {store.catalogErrors.agents}
                        {agents ? ' — showing the last list received.' : ''}
                    </S.Empty>
                )}
                <S.Scroll>
                    <S.Row header={true} columns={AGENT_COLUMNS} min={860}>
                        <span>Name</span>
                        <span>Invocation model</span>
                        <span>Agent ID</span>
                        <span>Streaming</span>
                        <span>Availability</span>
                        <span>Used by</span>
                    </S.Row>
                    {(agents ?? []).map(a => {
                        const ready = isAgentReady(a);
                        return (
                            <S.Row key={a.id} columns={AGENT_COLUMNS} min={860}>
                                <S.Strong>{a.name}</S.Strong>
                                <S.Mono>{a.invocationModel}</S.Mono>
                                <S.Mono>{a.id}</S.Mono>
                                <span>{a.streamingSupported ? 'Yes' : 'No'}</span>
                                <S.Cell>
                                    <span>
                                        <StatusTag tone={ready ? 'success' : 'danger'}>{a.availability}</StatusTag>
                                    </span>
                                    {a.unavailableReason && <S.Muted>{a.unavailableReason}</S.Muted>}
                                </S.Cell>
                                <S.Mono>{saved ? usedBy(profilesUsingAgent(saved, a.id)) : '—'}</S.Mono>
                            </S.Row>
                        );
                    })}
                    {agents && agents.length === 0 && <S.Empty>No agents are registered in this environment.</S.Empty>}
                    {!agents && !store.catalogErrors.agents && <S.Empty>Loading agents…</S.Empty>}
                </S.Scroll>
            </S.Table>

            <S.Table>
                <S.TableHead>
                    <S.Grow>
                        <S.CardTitle>Models</S.CardTitle>
                        <S.CardSub>
                            Direct model_name aliases configured in the gateway. Agent names excluded. Listing does not
                            guarantee support for every operation.
                        </S.CardSub>
                    </S.Grow>
                </S.TableHead>
                {store.catalogErrors.models && (
                    <S.Empty>The model list could not be loaded: {store.catalogErrors.models}</S.Empty>
                )}
                <S.Scroll>
                    {(models ?? []).map(m => (
                        <S.Row key={m} columns={MODEL_COLUMNS} min={400}>
                            <S.Mono>{m}</S.Mono>
                            <S.Mono>{saved ? usedBy(profilesUsingModel(saved, m)) : '—'}</S.Mono>
                        </S.Row>
                    ))}
                    {models && models.length === 0 && <S.Empty>No models are configured in this environment.</S.Empty>}
                    {!models && !store.catalogErrors.models && <S.Empty>Loading models…</S.Empty>}
                </S.Scroll>
            </S.Table>
        </S.Stack>
    );
});
